/**
 * The 3PL's bulk "Stop offering blanks" from the /products floating bar
 * (Phase 92, UI-SPEC 3.3; D-23), on the `ArchiveSkusDialog` shape: the usage
 * check runs first, then the review, and only the cleared blanks are sent.
 *
 * One `previewStopOfferingBlanks` read when the dialog opens puts each blank
 * in exactly one group (92-30 review M2):
 * - "Still used by client products, replace first": client products print on
 *   it, or a back-up mapping uses it. Listed, never sent.
 * - "Waiting for orders already placed": nothing new uses it, but orders
 *   already placed still hold it. It cannot stop being offered yet. Listed,
 *   never sent.
 * - "Clients can no longer choose these": sent, with `skipInUse`, so the
 *   server leaves any blank that came into use since the check shared and
 *   says how many (`skippedInUseCount`).
 *
 * Calls (admins only; the dialog never renders for anyone else): the one
 * review read per open (and per "Check usage again"), then one
 * `bulkSetSkuShared` per 200 cleared blanks, in turn. No polling.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { NoteLine } from '@/components/shared/NoteLine';
import { SectionError } from '@/components/shared/SectionError';
import {
  isWaitingForOrdersError,
  usePreviewStopOfferingBlanks,
  useStopOfferingBlanks,
} from '../../hooks/useSharedSkus';

/** The review read's bound (`previewStopOfferingBlanksSchema`). */
const REVIEW_MAX_IDS = 500;
/** The write's bound (`bulkSetSkuSharedSchema`). */
const WRITE_MAX_IDS = 200;

export interface StopOfferingTarget {
  id: string;
  sku: string;
}

export interface StopOfferingBlanksDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The selected blanks the caller shares (the bar passes only those). */
  selectedSkus: StopOfferingTarget[];
  /** The pass finished and the dialog closed: refresh and clear the selection. */
  onDone?: () => void;
  /** A pass failed part way: something may have changed, so refresh. */
  onPartialFailure?: () => void;
}

function SkuGroup({
  heading,
  hint,
  items,
}: {
  heading: string;
  hint?: string;
  items: Array<{ id: string; sku: string; lines?: string[] }>;
}) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{heading}</p>
      {hint && <p className="text-xs leading-normal text-muted-foreground">{hint}</p>}
      <ul className="space-y-1">
        {items.map((item) => (
          <li key={item.id} className="min-w-0 text-sm">
            <span className="block break-all font-mono">{item.sku}</span>
            {item.lines && item.lines.length > 0 && (
              <span className="block text-xs leading-normal text-muted-foreground">{item.lines.join(' · ')}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function StopOfferingBlanksDialog({
  open,
  onOpenChange,
  selectedSkus,
  onDone,
  onPartialFailure,
}: StopOfferingBlanksDialogProps) {
  const { t } = useTranslation('inventory');
  // Snapshot at open (the caller mounts this only while open): a refetch
  // behind the dialog never rewrites the list the operator is reading.
  const [targets] = useState(() => selectedSkus);
  // The bar button that opened it: with no trigger, Radix would send focus to
  // the body on close, so it is returned here.
  const [returnFocus] = useState(() =>
    typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null),
  );
  const tooMany = targets.length > REVIEW_MAX_IDS;
  const ids = useMemo(() => (tooMany ? null : targets.map((target) => target.id)), [targets, tooMany]);
  const review = usePreviewStopOfferingBlanks(ids);
  const { stopOffering } = useStopOfferingBlanks();
  const [submitting, setSubmitting] = useState(false);

  const skuOf = useMemo(() => new Map(targets.map((target) => [target.id, target.sku])), [targets]);
  const data = review.error ? undefined : review.data;
  const loading = !tooMany && !review.error && (review.loading || !review.data);

  const inUse = (data?.inUse ?? []).map((entry) => ({
    id: entry.skuId,
    sku: skuOf.get(entry.skuId) ?? entry.skuId,
    lines: [
      ...(entry.clientProducts > 0 ? [t('sharedSkus.replace.usedBy', { count: entry.clientProducts })] : []),
      ...(entry.backupMappings > 0 ? [t('sharedSkus.bulk.backupLine', { count: entry.backupMappings })] : []),
    ],
  }));
  const waiting = (data?.waitingForOrders ?? []).map((entry) => ({
    id: entry.skuId,
    sku: skuOf.get(entry.skuId) ?? entry.skuId,
  }));
  const clear = (data?.clear ?? []).map((id) => ({ id, sku: skuOf.get(id) ?? id }));
  const confirmCount = data ? clear.length : targets.length;
  const confirmDisabled = loading || submitting || !data || clear.length === 0;

  const runPass = async () => {
    const send = clear.map((item) => item.id);
    if (send.length === 0) return;
    setSubmitting(true);
    let updated = 0;
    let skipped = 0;
    let failure: unknown = null;
    for (let i = 0; i < send.length; i += WRITE_MAX_IDS) {
      try {
        const result = await stopOffering(send.slice(i, i + WRITE_MAX_IDS));
        updated += result.updatedCount;
        skipped += result.skippedInUseCount ?? 0;
      } catch (error) {
        failure = error;
        break;
      }
    }
    setSubmitting(false);

    if (updated > 0) toast.success(t('sharedSkus.bulk.stopDone', { count: updated }));
    // UI 38: blanks that came into use since the check stayed offered.
    if (skipped > 0) toast.warning(t('sharedSkus.bulk.stopSkipped', { count: skipped }), { duration: Infinity });

    if (failure !== null) {
      // Plain copy, never the raw message. Stay open on a fresh check.
      if (isWaitingForOrdersError(failure)) toast.warning(t('sharedSkus.bulk.stopWaiting'), { duration: Infinity });
      else toast.error(t('sharedSkus.bulk.stopFailed'), { duration: Infinity });
      onPartialFailure?.();
      review.retry();
      return;
    }
    onDone?.();
    onOpenChange(false);
  };

  let body;
  if (tooMany) {
    body = (
      <NoteLine tone="warning" icon={AlertTriangle} className="leading-normal">
        {t('sharedSkus.bulk.tooManyBlanks', { max: REVIEW_MAX_IDS })}
      </NoteLine>
    );
  } else if (review.error && !review.loading) {
    // UI 37
    body = (
      <SectionError
        variant="compact"
        body={t('sharedSkus.bulk.stopLoadError')}
        onRetry={review.retry}
        retryLabel={t('sharedSkus.bulk.stopRetry')}
      />
    );
  } else if (loading) {
    // UI 36
    body = (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
        {t('sharedSkus.bulk.stopLoading')}
      </p>
    );
  } else {
    body = (
      <>
        {clear.length === 0 && (
          <NoteLine tone="warning" icon={AlertTriangle} className="leading-normal">
            {t('sharedSkus.bulk.noneClear')}
          </NoteLine>
        )}
        {inUse.length > 0 && <SkuGroup heading={t('sharedSkus.bulk.groupInUse')} items={inUse} />}
        {waiting.length > 0 && (
          <SkuGroup
            heading={t('sharedSkus.bulk.groupWaiting')}
            hint={t('sharedSkus.bulk.groupWaitingHint')}
            items={waiting}
          />
        )}
        {clear.length > 0 && <SkuGroup heading={t('sharedSkus.bulk.groupClear')} items={clear} />}
      </>
    );
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && submitting) return;
        onOpenChange(next);
      }}
    >
      <AlertDialogContent
        className="sm:max-w-xl"
        onEscapeKeyDown={(event) => {
          if (submitting) event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (returnFocus?.isConnected) returnFocus.focus();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{t('sharedSkus.bulk.stopTitle', { count: targets.length })}</AlertDialogTitle>
          <AlertDialogDescription>{t('sharedSkus.bulk.stopDescription')}</AlertDialogDescription>
        </AlertDialogHeader>

        {/* -mx-px px-px pt-px: room for rings the scroll box would clip. */}
        <div className="-mx-px max-h-[55vh] min-w-0 space-y-4 overflow-y-auto px-px pt-px" aria-busy={loading}>
          <p role="status" className="sr-only">
            {loading ? t('sharedSkus.bulk.stopLoading') : ''}
          </p>
          {body}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>{t('sharedSkus.bulk.stopKeep')}</AlertDialogCancel>
          {/* A reversible removal: orange outline (DESIGN §3.4, §13.6). */}
          <Button
            type="button"
            variant="outline"
            className="gap-1.5 border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
            disabled={confirmDisabled}
            onClick={() => void runPass()}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
            {t('sharedSkus.bulk.stopConfirm', { count: confirmCount })}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
